import React, { useEffect, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import './Login.css';

interface LoginProps {
  role: 'player' | 'artist';
  onBack: () => void;
  onLoginSuccess: (token: string, user: any) => void;
}

type Mode = 'login' | 'register' | 'reset';
type ResetStep = 'request' | 'confirm';

const Login: React.FC<LoginProps> = ({ role, onBack, onLoginSuccess }) => {
  const { t, language } = useLanguage();
  const [mode, setMode] = useState<Mode>('login');
  const [resetStep, setResetStep] = useState<ResetStep>('request');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [devToken, setDevToken] = useState('');
  const [resetSent, setResetSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('resetToken');
    if (tokenParam) {
      setMode('reset');
      setResetStep('confirm');
      setResetToken(tokenParam);
    }
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (password.length < 8) {
      setLoading(false);
      setError(t.login.passwordMin);
      return;
    }
    if (password !== confirmPassword) {
      setLoading(false);
      setError(t.login.passwordMismatch);
      return;
    }

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, role, language }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLoginSuccess(data.token, data.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Login failed');
      }

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      onLoginSuccess(data.token, data.user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send reset link');
      }

      if (data.token) {
        setDevToken(data.token);
      }
      setResetSent(true);
      setInfo(t.login.resetEmailSent);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setInfo('');

    if (password.length < 8) {
      setLoading(false);
      setError(t.login.passwordMin);
      return;
    }
    if (password !== confirmPassword) {
      setLoading(false);
      setError(t.login.passwordMismatch);
      return;
    }

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Reset failed');
      }

      setMode('login');
      setResetStep('request');
      setResetToken('');
      setPassword('');
      setConfirmPassword('');
      setInfo(t.login.resetSuccess);
      setResetSent(false);

      const url = new URL(window.location.href);
      if (url.searchParams.has('resetToken')) {
        url.searchParams.delete('resetToken');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === 'reset') return t.login.resetTitle;
    return role === 'artist' ? t.login.artistTitle : t.login.playerTitle;
  };

  return (
    <div className="login">
      <div className="login-bg">
        <div className="login-orb"></div>
      </div>

      <header className="login-header">
        <button className="game-back" onClick={onBack}>&lt;</button>
        <h1>{getTitle()}</h1>
        <div style={{ width: 40 }}></div>
      </header>

      <main className="login-content">
        <div className="login-card">
          <div className="login-icon">
            {mode === 'reset' ? '🔑' : role === 'artist' ? '🎧' : '🎮'}
          </div>

          {mode === 'login' && (
            <>
              <h2>{role === 'artist' ? t.login.artistTitle : t.login.playerTitle}</h2>
              <p className="login-subtitle">{t.login.loginSubtitle}</p>
            </>
          )}

          {mode === 'register' && (
            <>
              <h2>{t.login.createAccount}</h2>
              <p className="login-subtitle">{t.login.registerSubtitle}</p>
            </>
          )}

          {mode === 'reset' && (
            <>
              <h2>{t.login.resetTitle}</h2>
              <p className="login-subtitle">
                {resetStep === 'request' ? t.login.resetSubtitle : t.login.resetConfirmSubtitle}
              </p>
            </>
          )}

          {error && <div className="login-error">{error}</div>}
          {info && <div className="login-success">{info}</div>}

          {mode === 'login' && (
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="email">{t.login.email}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.login.emailPlaceholder}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">{t.login.password}</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.login.passwordPlaceholder}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? t.common.loading : t.login.loginButton}
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setMode('reset');
                  setResetStep('request');
                  setResetSent(false);
                  setInfo('');
                  setError('');
                }}
              >
                {t.login.forgotPassword}
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setMode('register');
                  setInfo('');
                  setError('');
                }}
              >
                {t.login.switchToRegister}
              </button>
            </form>
          )}

          {mode === 'register' && (
            <form onSubmit={handleRegister}>
              <div className="form-group">
                <label htmlFor="email">{t.login.email}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.login.emailPlaceholder}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="register-password">{t.login.password}</label>
                <input
                  id="register-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.login.passwordPlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">{t.login.confirmPassword}</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t.login.passwordPlaceholder}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? t.common.loading : t.login.registerButton}
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setMode('login');
                  setInfo('');
                  setError('');
                }}
              >
                {t.login.switchToLogin}
              </button>
            </form>
          )}

          {mode === 'reset' && resetStep === 'request' && (
            <form onSubmit={handleRequestPasswordReset}>
              <div className="form-group">
                <label htmlFor="reset-email">{t.login.email}</label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.login.emailPlaceholder}
                  required
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? t.common.loading : t.login.requestReset}
              </button>
              {resetSent && (
                <div className="reset-success">
                  {devToken && (
                    <div className="dev-token">
                      <p>Dev mode - reset token:</p>
                      <code>{devToken.substring(0, 20)}...</code>
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-full"
                    onClick={() => setResetStep('confirm')}
                    style={{ marginTop: 10 }}
                  >
                    {t.login.haveResetToken}
                  </button>
                </div>
              )}
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setMode('login');
                  setResetStep('request');
                  setResetSent(false);
                  setInfo('');
                  setError('');
                }}
              >
                {t.common.back}
              </button>
            </form>
          )}

          {mode === 'reset' && resetStep === 'confirm' && (
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label htmlFor="reset-token">{t.login.resetToken}</label>
                <input
                  id="reset-token"
                  type="text"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value)}
                  placeholder={t.login.resetTokenPlaceholder}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-password">{t.login.newPassword}</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.login.passwordPlaceholder}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="confirm-new-password">{t.login.confirmPassword}</label>
                <input
                  id="confirm-new-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t.login.passwordPlaceholder}
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={loading}
              >
                {loading ? t.common.loading : t.login.resetPassword}
              </button>
              <button
                type="button"
                className="btn-link"
                onClick={() => setResetStep('request')}
              >
                {t.common.back}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
};

export default Login;
